<template>
  <aside class="sidebar w-64 md:shadow transform -translate-x-full md:translate-x-0 transition-transform duration-150 ease-in bg-white">
    <div class="sidebar-content">
      <ul class="flex flex-col w-full">
        <li class="my-px">
          <side-bar-section title="Selected Layers" show-carrot="true">
            <div v-for="layer in selection" :key="layer.id">
              <div class="flex py-2 pl-2 w-full flow-root focus:outline-none hover:bg-gray-200">
                <div class="inline-block w-full font-medium text-left px-2 uppercase">
                  <router-link class="text-gray-700" :to="'/layers/' + layer.id"
                    ><i class="pr-2 fas" :class="getLayerIcon(layer.resource_type)" /> {{ layer.title }}</router-link
                  >
                  <button class="float-right" @click="removeFromSelection(layer)">
                    <i class="float-right fas fa-delete-left items-center py-1 text-gray-300 hover:text-white" />
                  </button>
                </div>
              </div>
            </div>
            <div
              v-if="selection.length"
              class="flex py-2 w-full flow-root focus:outline-none text-white bg-blue-700 opacity-50 rounded hover:bg-gray-200 hover:text-black"
            >
              <router-link class="inline-block w-full font-medium text-center uppercase rounded" to="/Map"
                ><i class="pr-2 fas fa-map-location" /> Export To Map <i class="pl-2 fas fa-arrow-right"
              /></router-link>
            </div>
          </side-bar-section>
        </li>
        <li class="my-px">
          <side-bar-section title="Type">
            <button
              v-if="resourceTypes.vector"
              class="flex text-left p-2 w-full focus:outline-none hover:bg-gray-200"
              @click="setResourceType(ResourceType.VectorLayer)"
            >
              <span class="flex flex-row gap-1 items-center text-gray-700">
                <span class="flex items-center justify-center">
                  <i class="fa-solid fa-vector-square"></i>
                </span>
                <p class="uppercase font-medium">
                  Vector Layers <span class="text-blue-700 font-light">({{ resourceTypes.vector }})</span>
                </p>
              </span>
            </button>
            <button
              v-if="resourceTypes.raster"
              class="flex text-left py-2 pl-2 w-full focus:outline-none hover:bg-gray-200"
              @click="setResourceType(ResourceType.RasterLayer)"
            >
              <span class="flex flex-row gap-1 items-center text-gray-700">
                <span class="flex items-center justify-center">
                  <i class="fa-solid fa-image"></i>
                </span>
                <p class="uppercase font-medium">
                  Raster Layers <span class="text-blue-700 font-light">({{ resourceTypes.raster }})</span>
                </p>
              </span>
            </button>
            <button
              v-if="resourceTypes.table"
              class="flex text-left py-2 pl-2 w-full focus:outline-none hover:bg-gray-200"
              @click="setResourceType(ResourceType.Table)"
            >
              <span class="flex flex-row gap-1 items-center text-gray-700">
                <span class="flex items-center justify-center">
                  <i class="fas fa-table-cells"></i>
                </span>
                <p class="uppercase font-medium">
                  Table <span class="text-blue-700 font-light">({{ resourceTypes.table }})</span>
                </p>
              </span>
            </button>
            <button
              v-if="resourceTypes.notebook"
              class="flex text-left py-2 pl-2 w-full focus:outline-none hover:bg-gray-200"
              @click="setResourceType(ResourceType.PythonNotebook)"
            >
              <span class="flex flex-row gap-1 items-center text-gray-700">
                <span class="flex items-center justify-center">
                  <i class="fab fa-python"></i>
                </span>
                <p class="uppercase font-medium">
                  Python Notebooks <span class="text-blue-700 font-light">({{ resourceTypes.notebook }})</span>
                </p>
              </span>
            </button>
            <button
              v-if="resourceTypes.application"
              class="flex text-left py-2 pl-2 w-full focus:outline-none hover:bg-gray-200"
              @click="setResourceType(ResourceType.ExternalApplication)"
            >
              <span class="flex flex-row gap-1 items-center text-gray-700">
                <span class="flex items-center justify-center">
                  <i class="fas fa-gauge-high"></i>
                </span>
                <p class="uppercase font-medium">
                  Applications <span class="text-blue-700 font-light">({{ resourceTypes.application }})</span>
                </p>
              </span>
            </button>
            <button
              v-if="resourceTypes.scenario"
              class="flex text-left py-2 pl-2 w-full focus:outline-none hover:bg-gray-200"
              @click="setResourceType(ResourceType.Scenario)"
            >
              <span class="flex flex-row gap-1 items-center text-gray-700">
                <span class="flex items-center justify-center">
                  <i class="fas fa-note-sticky"></i>
                </span>
                <p class="uppercase font-medium">
                  Use Cases <span class="text-blue-700 font-light">({{ resourceTypes.scenario }})</span>
                </p>
              </span>
            </button>
          </side-bar-section>
        </li>
        <li class="my-px">
          <side-bar-section title="Categories" show-carrot="true">
            <div v-for="cat in categories" :key="cat.identifier">
              <button @click="appendCategory(cat.identifier)" class="flex text-left py-2 pl-2 w-full focus:outline-none hover:bg-gray-200">
                <span class="flex flex-row gap-1 items-center text-gray-700">
                  <!--<span class="flex items-center justify-center">
                      <i :class='cat.fa_class'/>
                    </span>-->
                  <p class="uppercase font-medium">
                    {{ cat.gn_description }} <span class="text-blue-700 font-light">({{ cat.count }})</span>
                  </p>
                </span>
              </button>
            </div>
          </side-bar-section>
        </li>
      </ul>
    </div>
  </aside>
</template>

<script>
import { computed, onBeforeMount, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'
import SideBarSection from '@/components/list-view-components/ListViewSideBarSection'
import { RouteNames } from '@/router'
import { ResourceType } from '@/services/common/enums'
import icons from '@/assets/resource-type-icons'

export default {
  name: 'ListViewSidebar',
  components: { SideBarSection },
  setup() {
    const route = useRoute()
    const store = useStore()
    const categories = computed(() => {
      return store.getters['Resources/categories']
    })

    const resourceTypes = computed(() => {
      return store.getters['Resources/resourceTypeSummary']
    })

    const selection = computed(() => {
      return store.state.Map.layers
    })

    const currentSearch = computed(() => {
      return store.getters['Resources/currentRequest']
    })

    function getLayerIcon(layerType) {
      return icons[layerType].icon
    }

    function appendCategory(identifier) {
      store.dispatch('Resources/addCategory', identifier)
    }

    function setResourceType(resourceType) {
      store.dispatch('Resources/setResourceType', resourceType)
    }

    function removeFromSelection(id) {
      store.dispatch('Map/removeLayer', id)
    }
    return {
      categories,
      resourceTypes,
      currentSearch,
      appendCategory,
      setResourceType,
      ResourceType,
      removeFromSelection,
      selection,
      getLayerIcon,
    }
  },
}
</script>

<style scoped>

</style>
